<script setup lang="ts">
import { onMounted, shallowRef } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterAuditLogsStore} from "@/charter/stores/charter-audit-logs-store.ts";

const charterAuditLogsStore = useCharterAuditLogsStore();

const myTheme = themeQuartz.withParams({});

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
  },
  columnDefs: [
    { field: "created", headerName: "Date", filter: true },
    { field: "userObjectId", headerName: "User ID", filter: true },
    { field: "email", headerName: "E-mail", filter: true },
    { field: "action", headerName: "Action", filter: true },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);

onMounted(async () => {
  await charterAuditLogsStore.fetchAuditLogs();
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

function exportData() {
  const date = new Date();
  gridApi.value!.exportDataAsExcel({
    fileName: "charter-audit-logs_" + date.toLocaleString()
  });
}

function refreshData() {
  charterAuditLogsStore.fetchAuditLogs();
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h1>Charter Audit Logs</h1>
      <div class="flex gap-2">
        <p-button link @click="refreshData" :loading="charterAuditLogsStore.isLoading">
          <span class="material-icons" title="refresh">refresh</span>
        </p-button>
        <p-button link @click="exportData" :disabled="!charterAuditLogsStore.hasAuditLogs">
          <span class="material-icons" title="download">download</span>
        </p-button>
      </div>
    </div>

    <div v-if="charterAuditLogsStore.hasError" class="p-message p-message-error">
      <div class="p-message-wrapper">
        <span class="p-message-icon pi pi-times-circle"></span>
        <div class="p-message-text">
          {{ charterAuditLogsStore.error }}
        </div>
        <button
          class="p-message-close p-link"
          @click="charterAuditLogsStore.clearError"
        >
          <span class="pi pi-times"></span>
        </button>
      </div>
    </div>

    <AgGridVue
      :rowData="charterAuditLogsStore.auditLogs"
      :loading="charterAuditLogsStore.isLoading"
      :gridOptions="gridOptions"
      ref="gridRef"
      :theme="myTheme"
      style="width: 100%; height: 100%"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
h1 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

.p-message {
  margin: 0;
}
</style>
